// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import React, { ReactNode } from "react";
import Typography, { TypographyProps } from "@material-ui/core/Typography";

import DeclarativeItemRenderer from "components/DeclarativeComponents/DeclarativeItemRenderer";
import { EditableProps } from "components/Editor/EditablePropsType";
import MarkDown from "./MarkDown";
import { getDefaultQuestionDefinition } from "./Questionnaire/QuestionnaireUtils";
import makeStyles from "@material-ui/core/styles/makeStyles";

const useStyles = makeStyles(
  theme => ({
    root: {
      marginBottom: "1rem",
    },
  }),
  { name: "Paragraph" }
);

interface ParagraphProps extends TypographyProps {
  content?: string | Array<string>;
  children?: ReactNode;
}

export const ParagraphEditableProps: EditableProps = {
  content: {
    ...getDefaultQuestionDefinition(),
    name: "content",
    label: "content",
    mandatory: true,
    valueType: "string",
  },
};

export type ParagraphComponent = (props: ParagraphProps) => JSX.Element;

const Paragraph: ParagraphComponent = (props: ParagraphProps) => {
  const { content, children, id, ...rest } = props;
  const classes = useStyles();

  if (!content && !children) {
    return <></>;
  }
  if (content) {
    let _content = content;
    if (!Array.isArray(_content)) {
      _content = [_content];
    }
    return (
      <div id={id}>
        {_content.map((paragraph: string, index: number): any => {
          return (
            <Paragraph key={"Paragraph_" + index} {...rest}>
              {paragraph}
            </Paragraph>
          );
        })}
      </div>
    );
  }

  return (
    <Typography variant="body2" id={id} className={classes.root} {...rest}>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        // If a children will end up rendering as a <MarkDown />
        // we need to make sure it won't use <p /> for the default element
        // wrapping text.
        if (child.type === MarkDown) {
          child.props.paragraphComponent = "span";
        } else if (child.type === DeclarativeItemRenderer) {
          const itemToRender = child.props.item;
          if (itemToRender.type === "MarkDown") {
            itemToRender.props.paragraphComponent = "span";
          }
        }
        return child;
      })}
    </Typography>
  );
};

export default Paragraph;
